import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import CustomInput from '../../../components/common/CustomInput';
import CustomSelect from '../../../components/common/CustomSelect';
import CustomFile from '../../../components/common/CustomFile';
import CustomCheckBox from '../../../components/common/CustomCheckBox';
import * as Yup from 'yup';
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance';
import axiosCommonInstance from '../../../utils/axios/axiosCommonInstance';

// Validation schema
const validationSchema = Yup.object({
  company: Yup.string().trim().required('Company Name is required'),
  category: Yup.string().trim().required('Category is required'),
  file: Yup.mixed().required('360 File is required'),
});

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const formik = useFormik({
    initialValues: {
      company: '',
      name: '',
      file: null,
      roomData: [
        {
          isWall: false,
          roomCoordinate: [
            {
              position: { x: 0, y: 0, z: 0 },
              rotation: { x: 0, y: 0, z: 0 },
              scale: { x: 1, y: 1, z: 1 },
              size: 0,
            },
          ],
        },
      ],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const error = await formik.validateForm();
      if (Object.keys(error).length === 0) {
        handleSubmit(values, { resetForm });
      } else {
        formik.setTouched(error);
      }
    },
  });

  // Fetch company data
  const fetchCompanyData = async () => {
    try {
      const response = await axiosAuthInstance.get('company/dropdown');
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }));
        setCompanyOptions(transformedData);
      }
    } catch (error) {
      console.log('Error fetching company data:', error);
    }
  };

  // Fetch existing data
  const fetchData = async () => {
    try {
      const response = await axiosCommonInstance.get(`dynamic360/detail/${id}`);
      if (response.data && response.data.result) {
        const dynamic360Data = response.data.result;
        formik.setValues({
          company: dynamic360Data.company._id,
          category: dynamic360Data.category._id,
          file: dynamic360Data.file,
          roomData: dynamic360Data.roomData
        });
        setLoading(false);
      }
    } catch (error) {
      console.log('Error fetching dynamic360 data:', error);
      setLoading(false);
    }
  };

  const fetchCategoryData = async () => {
    try {
      const response = await axiosCommonInstance.get('library-category/dropdown');
      if (response?.status === 200) {
        const transformedData = response.data.result.map((item) => ({
          value: item._id,
          label: item.name,
        }));
        setCategoryOptions(transformedData);
      }
    } catch (error) {
      console.error('Error fetching Category Data', error);
    }
  }

  useEffect(() => {
    fetchCompanyData();
    fetchData();
    fetchCategoryData();
  }, [id]);

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitted(true);
    try {
      const formData = new FormData();
      formData.append('company', values.company);
      formData.append('name', values.name);
      formData.append('file', values.file);
      formData.append('roomData', JSON.stringify(values.roomData));

      const response = await axiosAuthInstance.put(
        `dynamic360/update/${id}`,
        formData
      );
      if (response.status === 200) {
        resetForm();
        navigate('/dynamic-360');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitted(false);
    }
  };



  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <FormikProvider value={formik}>
      <form className="flex flex-col gap-6 p-4" onSubmit={formik.handleSubmit}>
        <div className="flex gap-4">
          <Field
            name="company"
            label="Company Name"
            placeholder="Select company"
            required
            component={CustomSelect}
            options={companyOptions}
          />

          <Field
            name='category'
            label='Select Category'
            required
            options={categoryOptions}
            component={CustomSelect}
          />
        </div>

        <Field
          name="file"
          label="Select 360 file"
          required
          accept='image/*'
          placeholder="Upload 360 File"
          component={CustomFile}
          url={formik.values.file}
        />

        <div className="border p-4 rounded-lg shadow-sm">
          <h3 className="font-semibold mb-2">Room Data</h3>
          {formik.values.roomData.map((room, rIndex) => (
            <div key={rIndex} className="mb-6 border p-4 rounded-md bg-gray-50">
              <div className='flex justify-between items-center'>
                <Field
                  name={`roomData[${rIndex}].isWall`}
                  label="Is Wall"
                  component={CustomCheckBox}
                  checked={room.isWall}
                />

                {rIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const updatedRoomData = [...formik.values.roomData];
                      updatedRoomData.splice(rIndex, 1);
                      formik.setFieldValue('roomData', updatedRoomData);
                    }}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                    title="Delete Room"
                  >
                    Remove
                  </button>
                )}
              </div>
              {room.roomCoordinate.map((coord, cIndex) => (
                <>
                  <div key={cIndex} className="grid grid-cols-3 gap-4 mt-4 bg-white p-3 rounded-md shadow">
                    <h4 className="col-span-3 font-medium text-gray-700">Coordinate #{cIndex + 1}</h4>

                    {/* Position */}
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].position.x`}
                      label="Position X"
                      type="number"
                      component={CustomInput}
                      required
                    />
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].position.y`}
                      label="Position Y"
                      type="number"
                      component={CustomInput}
                      required
                    />
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].position.z`}
                      label="Position Z"
                      type="number"
                      component={CustomInput}
                      required
                    />

                    {/* Rotation */}
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].rotation.x`}
                      label="Rotation X"
                      type="number"
                      component={CustomInput}
                      required
                    />
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].rotation.y`}
                      label="Rotation Y"
                      type="number"
                      component={CustomInput}
                      required
                    />
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].rotation.z`}
                      label="Rotation Z"
                      type="number"
                      component={CustomInput}
                      required
                    />

                    {/* Scale */}
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].scale.x`}
                      label="Scale X"
                      type="number"
                      component={CustomInput}
                      required
                    />
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].scale.y`}
                      label="Scale Y"
                      type="number"
                      component={CustomInput}
                      required
                    />
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].scale.z`}
                      label="Scale Z"
                      type="number"
                      component={CustomInput}
                      required
                    />

                    {/* Size */}
                    <Field
                      name={`roomData[${rIndex}].roomCoordinate[${cIndex}].size`}
                      label="Size"
                      type="number"
                      component={CustomInput}
                      required
                    />


                  </div>
                  <div className='flex justify-between items-center'>
                    <button
                      type="button"
                      onClick={() => {
                        const newCoordinate = {
                          position: { x: 0, y: 0, z: 0 },
                          rotation: { x: 0, y: 0, z: 0 },
                          scale: { x: 1, y: 1, z: 1 },
                          size: 0
                        };
                        const updatedRoomData = [...formik.values.roomData];
                        updatedRoomData[rIndex].roomCoordinate.push(newCoordinate);
                        formik.setFieldValue('roomData', updatedRoomData);
                      }}
                      className="mt-4 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                    >
                      Add Coordinate
                    </button>

                    {cIndex !== 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedRoomData = [...formik.values.roomData];
                          updatedRoomData[rIndex].roomCoordinate.splice(cIndex, 1);
                          formik.setFieldValue('roomData', updatedRoomData);
                        }}
                        className="mt-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </>
              ))}
            </div>
          ))}

          <button
            type="button"
            onClick={() => {
              const newRoom = {
                isWall: false,
                roomCoordinate: [
                  {
                    position: { x: 0, y: 0, z: 0 },
                    rotation: { x: 0, y: 0, z: 0 },
                    scale: { x: 1, y: 1, z: 1 },
                    size: 0
                  }
                ]
              };
              formik.setFieldValue('roomData', [...formik.values.roomData, newRoom]);
            }}
            className="mt-4 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
          >
            Add Room Data
          </button>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitted}
            className={`bg-orange-500 text-white px-6 py-2 rounded-lg text-lg font-medium ${isSubmitted
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-orange-600'
              }`}
          >
            {isSubmitted ? (
              <div className="w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent"></div>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </form>
    </FormikProvider>
  );
};

export default Edit;
